import * as React from "react"
import { cn } from "@/lib/utils"
import { Badge } from "./badge"
import type { LucideIcon } from "lucide-react"

export interface ViewTab {
  value: string      // 'all', 'active', etc.
  label: string      // 'All', 'Active', etc.
  count?: number     // Optional badge count
  icon?: LucideIcon  // Optional icon
}

export interface ViewTabsProps {
  views: ViewTab[]
  currentView: string
  onChange: (view: string) => void
  className?: string
  /**
   * Tighter gap + horizontal-scroll-instead-of-wrap, for a narrow host (e.g.
   * the ~448px stakeholder-portal Sheet drawer's 4-tab bar) where the default
   * gap-x-6 flex-wrap can wrap to two lines (visual-review finding,
   * 2026-07-24 final pass). Cosmetic-only; every other ViewTabs consumer is a
   * full-width page and is unaffected when this is omitted.
   */
  dense?: boolean
}

export function ViewTabs({ views, currentView, onChange, className, dense }: ViewTabsProps) {
  return (
    <div
      className={cn(
        "border-b border-gray-200",
        className
      )}
      role="tablist"
    >
      <nav
        className={cn(
          "flex px-1",
          dense
            ? "flex-nowrap gap-x-4 gap-y-1 overflow-x-auto"
            : "flex-wrap gap-x-6 gap-y-1"
        )}
        aria-label="View tabs"
      >
        {views.map((view) => {
          const isActive = currentView === view.value
          const Icon = view.icon

          return (
            <button
              key={view.value}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${view.value}-panel`}
              onClick={() => onChange(view.value)}
              className={cn(
                "flex shrink-0 items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                isActive
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
              )}
            >
              {Icon && <Icon className="h-4 w-4" />}
              <span>{view.label}</span>
              {view.count !== undefined && view.count > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {view.count}
                </Badge>
              )}
            </button>
          )
        })}
      </nav>
    </div>
  )
}
